const TOKEN_KEY = 'jwt';

/**
 * Decodes a JWT token and returns its payload
 * @param {string} token - The JWT token to decode
 * @returns {Object|null} The decoded payload or null if invalid
 */
export const decodeToken = (token) => {
    try {
        if (!token) {
            console.log('No token provided');
            return null;
        }

        // JWT tokens are in format: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            console.log('Invalid token format - expected 3 parts');
            return null;
        }

        const base64Url = parts[1];

        // Convert base64url to base64
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

        // Decode base64
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('Error decoding token:', error);
        return null;
    }
};

/**
 * Checks if a token is expired
 * @param {Object} decodedToken - The decoded JWT token
 * @returns {boolean} True if token is expired, false if valid or has no exp claim
 */
export const isTokenExpired = (decodedToken) => {
    if (!decodedToken?.exp) return false; // If no exp claim, token is valid

    const currentTime = Math.floor(Date.now() / 1000);
    console.log(currentTime);
    return decodedToken.exp < currentTime;
};

export const getToken = () => {
    return localStorage.getItem(TOKEN_KEY);
};

export const setToken = (token) => {
    localStorage.setItem(TOKEN_KEY, token);
};

export const removeToken = () => {
    localStorage.removeItem(TOKEN_KEY);
};

/**
 * Gets the current user profile from the JWT token
 * @returns {Object|null} The user profile or null if not logged in
 */
export const getUserProfile = () => {
    const token = getToken();
    if (!token) return null;

    const decoded = decodeToken(token);
    if (!decoded || isTokenExpired(decoded)) {
        removeToken();
        return null;
    }

    return {
        id: decoded.id,
        email: decoded.email,
        firstName: decoded.first_name,
        lastName: decoded.last_name,
        roles: decoded.roles,
        currentRole: decoded.current_role
    };
};

/**
 * Gets the current profile ID from JWT token (for WebSocket comparison)
 * This extracts the profile ID from the token's "id" field which is actually the profile ID
 * @returns {string|null} The profile ID or null if not logged in
 */
export const getCurrentProfileId = () => {
    const token = getToken();
    if (!token) return null;

    const decoded = decodeToken(token);
    if (!decoded || isTokenExpired(decoded)) {
        return null;
    }

    // The "id" in the JWT is actually the user ID, but we need profile ID
    // We'll need to derive it from the GraphQL context
    // For now, return the user's ID which we can use to match
    return decoded.id ? String(decoded.id) : null;
}; 
